/**
 * search-collection-widget.js
 *
 * Activates every .js-search-collection-banner trigger element on the
 * page: reads its data-query, checks the curated keyword -> collection
 * map, falls back to Shopify's native predictive-search collection
 * match, and renders a banner in place if either finds something.
 *
 * Used in two places:
 *   1. The full /search results page -- one trigger present at load,
 *      scanned once on DOMContentLoaded.
 *   2. The header predictive-search dropdown -- its results container
 *      is fully replaced (innerHTML) on every keystroke, so a fresh
 *      trigger element appears each time. A MutationObserver watches
 *      for that and activates each new one as it shows up.
 *
 * Requires search-collection-widget-assets.liquid to be rendered once,
 * sitewide (supplies window.SearchCollectionMapURL and this file).
 */
(function () {
  var BANNER_SELECTOR = '.js-search-collection-banner';
  var MAP_ASSET_URL = window.SearchCollectionMapURL;
  var DONE_ATTR = 'data-scw-done';
  var mapPromise = null;

  function normalize(str) {
    return (str || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ');
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function renderBanner(container, title, url) {
    container.innerHTML =
      '<div class="search-collection-banner">' +
      '<span>Looking for our full <strong>' + escapeHtml(title) + '</strong> collection?</span>' +
      '<a href="' + url + '" class="search-collection-banner__link">View Collection &rarr;</a>' +
      '</div>';
    container.hidden = false;
  }

  function findBestMatch(map, normalizedQuery) {
    if (map[normalizedQuery]) return map[normalizedQuery];

    var bestKey = null;
    for (var key in map) {
      if (key === '_readme') continue;
      if (normalizedQuery.indexOf(key) !== -1) {
        if (!bestKey || key.length > bestKey.length) bestKey = key;
      }
    }
    return bestKey ? map[bestKey] : null;
  }

  function getMap() {
    if (!MAP_ASSET_URL) return Promise.resolve(null);
    if (!mapPromise) {
      mapPromise = fetch(MAP_ASSET_URL).then(function (res) { return res.json(); });
    }
    return mapPromise;
  }

  function tryCuratedMap(query, container, onMiss) {
    getMap()
      .then(function (map) {
        if (!map) { onMiss(); return; }
        var entry = findBestMatch(map, normalize(query));
        if (entry && entry.handle) {
          renderBanner(container, entry.title, '/collections/' + entry.handle);
        } else {
          onMiss();
        }
      })
      .catch(onMiss);
  }

  function tryPredictiveSearch(query, container) {
    fetch('/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=collection&resources[limit]=1')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var collections = data && data.resources && data.resources.results && data.resources.results.collections;
        if (collections && collections.length) {
          var c = collections[0];
          renderBanner(container, c.title, c.url);
        }
      })
      .catch(function () { /* fail silently -- results still render normally */ });
  }

  function activate(container) {
    if (container.getAttribute(DONE_ATTR) === 'true') return;
    container.setAttribute(DONE_ATTR, 'true');

    var query = container.getAttribute('data-query');
    if (!query) return;

    tryCuratedMap(query, container, function () {
      tryPredictiveSearch(query, container);
    });
  }

  function scan(root) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return; // Element or Document
    if (root.nodeType === 1 && root.matches && root.matches(BANNER_SELECTOR)) activate(root);
    if (root.querySelectorAll) {
      var nodes = root.querySelectorAll(BANNER_SELECTOR);
      for (var i = 0; i < nodes.length; i++) activate(nodes[i]);
    }
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      // DOMContentLoaded already fired (e.g. theme editor preview injects
      // scripts after the fact) -- run immediately instead of waiting
      // forever for an event that's already happened.
      fn();
    }
  }

  ready(function () {
    scan(document);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) scan(added[j]);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();